import { useState, type FormEvent } from "react";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  type Transition,
  type Variants,
} from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Grid3X3,
  LogIn,
  LogOut,
  Play,
  Plus,
  ShieldCheck,
  Target,
  UserRound,
  UsersRound,
  Zap,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useSegmentedIndicator } from "../../hooks/useSegmentedIndicator";
import type { ConnectionState } from "../../hooks/useSkyjoGame";
import { COLLAPSE } from "../../lib/motion";
import { Avatar } from "../ui/Avatar";
import { Brand } from "../ui/Brand";

type HomeMode = "create" | "join";

// Typage explicite : `as const` produirait un tuple readonly que framer refuse.
const EASE_OUT: [number, number, number, number] = [0.22, 1, 0.36, 1];
const EASE_IN: [number, number, number, number] = [0.4, 0, 1, 1];

/** Ressort partagé par la course du jeu de cartes et l'arrivée du panneau. */
const GLIDE: Transition = { type: "spring", stiffness: 210, damping: 26, mass: 0.9 };

const heroCardVariants: Variants = {
  hidden: { y: 40, rotate: 0, opacity: 0 },
  spread: (index: number) => ({
    y: 0,
    rotate: (index - 1.5) * 7,
    opacity: 1,
    transition: { delay: 0.18 + index * 0.07, type: "spring", stiffness: 130 },
  }),
  // Second temps : l'éventail se referme pour laisser la place au formulaire.
  tucked: (index: number) => ({
    y: 0,
    rotate: (index - 1.5) * 3.5,
    opacity: 1,
    transition: { delay: index * 0.045, type: "spring", stiffness: 200, damping: 19 },
  }),
};

interface HomeScreenProps {
  connection: ConnectionState;
  pendingAction: string | null;
  preferredName: string;
  onCreate: (name: string) => Promise<boolean>;
  onJoin: (code: string, name: string) => Promise<boolean>;
  onOpenAuth: () => void;
  /** Fourni dès que l'écran de profil existe ; le bloc identité s'y adapte. */
  onOpenProfile?: () => void;
}

export const HomeScreen = ({
  connection,
  pendingAction,
  preferredName,
  onCreate,
  onJoin,
  onOpenAuth,
  onOpenProfile,
}: HomeScreenProps) => {
  const { user, accountsEnabled, pending: authPending, logout } = useAuth();
  const reducedMotion = useReducedMotion();
  const compact = useMediaQuery("(max-width: 780px)");
  /** Sous ce gabarit le formulaire complet ne laisse plus de place aux cartes. */
  const cramped = useMediaQuery("(max-width: 360px) and (max-height: 600px)");
  const invitedCode = new URLSearchParams(window.location.search)
    .get("room")
    ?.toUpperCase()
    .replace(/[^A-Z2-9]/g, "")
    .slice(0, 8) ?? "";
  const [mode, setMode] = useState<HomeMode>(invitedCode ? "join" : "create");
  const [name, setName] = useState(preferredName);
  const [code, setCode] = useState(invitedCode);
  const [touched, setTouched] = useState(false);
  /**
   * Sur mobile l'accueil tient en deux temps pour ne jamais défiler : la
   * présentation, puis le formulaire. Une invitation par lien saute directement
   * au second, où se trouve l'action attendue.
   */
  const [formOpen, setFormOpen] = useState(() => invitedCode !== "");
  const modeTabs = useSegmentedIndicator<HTMLDivElement>(mode === "create" ? 0 : 1);
  /**
   * Atterrir d'emblée sur le formulaire n'a rien à animer : le panneau garde son
   * apparition d'origine tant que les deux temps n'ont pas été parcourus.
   */
  const [stepped, setStepped] = useState(false);
  const step = (open: boolean) => {
    setStepped(true);
    setFormOpen(open);
  };
  const showIntro = !compact || !formOpen;
  const showPanel = !compact || formOpen;
  const showDeck = !(compact && formOpen && cramped);
  const stepping = compact && stepped;
  /** Sous prefers-reduced-motion il reste un fondu, mais plus aucune attente. */
  const beat = (seconds: number) => (reducedMotion ? 0 : seconds);
  /**
   * Les lignes de présentation entrent du bas en cascade et repartent vers le
   * haut, la plus basse en premier : le bloc se décolle au lieu de s'éteindre.
   */
  const introRow = (row: number) => ({
    initial: { opacity: 0, y: 18 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -18, transition: { duration: 0.24, delay: beat((3 - row) * 0.04), ease: EASE_IN } },
    transition: { duration: 0.34, delay: beat(0.1 + row * 0.045), ease: EASE_OUT },
  });
  const busy = pendingAction === "create-room" || pendingAction === "join-room";
  // Le serveur impose le pseudo du compte : le champ disparaît une fois connecté.
  const tableName = user ? user.displayName : name;
  const nameValid = user !== null || (name.trim().length >= 1 && name.trim().length <= 24);
  const codeValid = /^[A-Z2-9]{4,8}$/.test(code);
  const formValid = nameValid && (mode === "create" || codeValid);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (!formValid || busy || connection !== "connected") return;
    if (mode === "create") void onCreate(tableName);
    else void onJoin(code, tableName);
  };

  return (
    <main className={`home-shell home-shell--${formOpen ? "form" : "intro"}`}>
      <header className="home-header">
        <Brand />
        <div className="home-header__aside">
          {user ? (
            <div className="account-chip">
              <button
                className="account-chip__identity"
                type="button"
                onClick={onOpenProfile}
                disabled={!onOpenProfile}
                aria-label={`Profil de ${user.handle}`}
              >
                <Avatar profile={user} size="sm" />
                <span>
                  <strong>{user.displayName}</strong>
                  <small>#{user.discriminator}</small>
                </span>
              </button>
              <button
                className="icon-button"
                type="button"
                onClick={() => void logout()}
                disabled={authPending === "logout"}
                aria-label="Se déconnecter"
                title="Se déconnecter"
              >
                {authPending === "logout" ? (
                  <span className="button-spinner" aria-hidden="true" />
                ) : (
                  <LogOut size={17} />
                )}
              </button>
            </div>
          ) : accountsEnabled ? (
            <button
              className="ghost-button ghost-button--compact account-cta"
              type="button"
              onClick={onOpenAuth}
              aria-label="Se connecter ou créer un compte"
            >
              <UserRound size={16} /> <span>Se connecter</span>
            </button>
          ) : null}
        </div>
      </header>

      <div className="home-layout">
        <motion.section
          className="hero-copy"
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.55, ease: EASE_OUT }}
        >
          {/* popLayout épingle le bloc sortant hors du flux dès la bascule : le
              panneau prend sa place à la première image au lieu d'attendre, et
              les deux hauteurs ne s'additionnent jamais. */}
          <AnimatePresence initial={false} mode="popLayout">
            {showIntro && (
              <motion.div className="home-intro-copy" key="intro-copy">
                <motion.span className="eyebrow" {...introRow(0)}>
                  <Target size={15} aria-hidden="true" />
                  Le jeu du plus petit score
                </motion.span>
                <motion.h1 {...introRow(1)}>
                  Révèle. Échange.<br />
                  <span>Vise plus bas.</span>
                </motion.h1>
                <motion.p className="hero-copy__lead" {...introRow(2)}>
                  Retrouve Skyjo en ligne dans une version fluide et animée. Invite tes proches, révèle les
                  bonnes cartes et termine avec le moins de points possible.
                </motion.p>
                <motion.div className="hero-metrics" aria-label="Informations sur le jeu" {...introRow(3)}>
                  <span><UsersRound size={17} /> 2–8 joueurs</span>
                  <span><Zap size={17} /> Temps réel</span>
                  <span><ShieldCheck size={17} /> {accountsEnabled ? "Compte ou invité" : "Sans compte"}</span>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence mode="popLayout">
            {showDeck && (
              /* Ce conteneur ne porte aucun transform en CSS : framer-motion peut
                 y écrire le sien sans effacer l'échelle de palier des cartes. */
              <motion.div
                className="hero-deck-slot"
                key="deck"
                layout={compact && !reducedMotion ? "position" : false}
                transition={{ layout: GLIDE }}
                exit={{ opacity: 0, scale: 0.88, transition: { duration: 0.2, ease: EASE_IN } }}
              >
                <div className="hero-deck" aria-hidden="true">
                  {[-2, 0, 7, 12].map((value, index) => (
                    <motion.span
                      className={`hero-card hero-card--${index}`}
                      key={value}
                      custom={index}
                      variants={heroCardVariants}
                      initial="hidden"
                      animate={compact && formOpen ? "tucked" : "spread"}
                    >
                      <small>{value}</small>
                      <strong>{value}</strong>
                      <i />
                    </motion.span>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Uniquement mobile, premier temps : le formulaire reste replié. */}
          <AnimatePresence initial={false} mode="popLayout">
            {showIntro && (
              <motion.div
                className="home-play"
                key="intro-play"
                initial={{ opacity: 0, y: 16, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 14, scale: 0.94, transition: { duration: 0.2, ease: EASE_IN } }}
                transition={{ duration: 0.34, delay: beat(0.28), ease: EASE_OUT }}
              >
                <button
                  className="primary-button primary-button--wide"
                  type="button"
                  onClick={() => step(true)}
                >
                  <Play size={19} fill="currentColor" aria-hidden="true" /> Jouer
                  <ArrowRight size={18} aria-hidden="true" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.section>

        <AnimatePresence mode="popLayout">
          {showPanel && (
            <motion.section
              className="join-panel glass-panel"
              key="join-panel"
              initial={{ opacity: 0, y: 28, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 22, scale: 0.97, transition: { duration: 0.22, ease: EASE_IN } }}
              transition={
                stepping
                  ? { ...GLIDE, delay: beat(0.1) }
                  : { delay: 0.08, duration: 0.5, ease: EASE_OUT }
              }
              aria-labelledby="join-title"
            >
              <div className="join-panel__halo" aria-hidden="true" />
              <motion.div
                className="join-panel__head"
                initial={stepping ? { opacity: 0, y: 12 } : false}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.32, delay: beat(0.2), ease: EASE_OUT }}
              >
                <button
                  className="icon-button icon-button--surface join-panel__back"
                  type="button"
                  onClick={() => step(false)}
                  aria-label="Revenir à la présentation"
                >
                  <ArrowLeft size={18} />
                </button>
                <span className="join-panel__icon"><Grid3X3 size={21} /></span>
                <div>
                  <h2 id="join-title">Prêt à jouer ?</h2>
                  <p>Crée ou rejoins une table en quelques secondes.</p>
                </div>
              </motion.div>

              <motion.div
                className="segmented-control"
                ref={modeTabs.containerRef}
                role="tablist"
                aria-label="Choisir une action"
                initial={stepping ? { opacity: 0, y: 12 } : false}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.32, delay: beat(0.25), ease: EASE_OUT }}
              >
                {modeTabs.indicator !== null && (
                  <motion.span
                    className="segmented-control__active"
                    animate={{
                      x: modeTabs.indicator.box.x,
                      y: modeTabs.indicator.box.y,
                      width: modeTabs.indicator.box.width,
                      height: modeTabs.indicator.box.height,
                    }}
                    transition={
                      modeTabs.indicator.animate
                        ? { type: "spring", stiffness: 420, damping: 36 }
                        : { duration: 0 }
                    }
                  />
                )}
                {(["create", "join"] as const).map((item) => (
                  <button
                    type="button"
                    role="tab"
                    aria-selected={mode === item}
                    className={mode === item ? "is-active" : ""}
                    onClick={() => {
                      setMode(item);
                      setTouched(false);
                    }}
                    key={item}
                  >
                    <span>{item === "create" ? "Créer une table" : "Rejoindre"}</span>
                  </button>
                ))}
              </motion.div>

              <motion.form
                className="join-form"
                onSubmit={submit}
                noValidate
                initial={stepping ? { opacity: 0, y: 12 } : false}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.32, delay: beat(0.3), ease: EASE_OUT }}
              >
                {user ? (
                  <div className="identity-note">
                    <Avatar profile={user} size="md" />
                    <span>
                      <small>Tu joues avec ton compte</small>
                      <strong>{user.handle}</strong>
                    </span>
                  </div>
                ) : (
                  <>
                    <label className="field-label" htmlFor="player-name">Ton prénom ou pseudo</label>
                    <div className={`text-field${touched && !nameValid ? " is-invalid" : ""}`}>
                      <span className="text-field__sigil" aria-hidden="true"><UserRound size={17} /></span>
                      <input
                        id="player-name"
                        autoComplete="nickname"
                        maxLength={24}
                        minLength={1}
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        onBlur={() => setTouched(true)}
                        placeholder="Camille"
                        aria-describedby={touched && !nameValid ? "name-error" : undefined}
                      />
                      <span className="text-field__count" aria-hidden="true">{name.length}/24</span>
                    </div>
                    {touched && !nameValid && (
                      <p className="field-error" id="name-error">Choisis un nom de 1 à 24 caractères.</p>
                    )}
                  </>
                )}

                <AnimatePresence initial={false}>
                  {mode === "join" && (
                    <motion.div
                      className="join-form__code"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={COLLAPSE}
                    >
                      <label className="field-label" htmlFor="room-code">Code de la table</label>
                      <div className={`text-field text-field--code${touched && !codeValid ? " is-invalid" : ""}`}>
                        <input
                          id="room-code"
                          autoComplete="off"
                          autoCapitalize="characters"
                          spellCheck={false}
                          inputMode="text"
                          maxLength={8}
                          value={code}
                          onChange={(event) =>
                            setCode(event.target.value.toUpperCase().replace(/[^A-Z2-9]/g, ""))
                          }
                          placeholder="JOUER7"
                          aria-describedby={touched && !codeValid ? "code-error" : undefined}
                        />
                      </div>
                      {touched && !codeValid && (
                        <p className="field-error" id="code-error">Le code contient 4 à 8 lettres ou chiffres (sans 0 ni 1).</p>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>

                <button
                  className="primary-button primary-button--wide"
                  type="submit"
                  disabled={busy || connection !== "connected"}
                >
                  {busy ? (
                    <><span className="button-spinner" aria-hidden="true" /> Connexion…</>
                  ) : mode === "create" ? (
                    <><Plus size={19} /> Créer ma table <ArrowRight size={18} /></>
                  ) : (
                    <><LogIn size={19} /> Rejoindre la table <ArrowRight size={18} /></>
                  )}
                </button>
                {connection !== "connected" && (
                  <p className="connection-help" role="status">
                    {connection === "offline"
                      ? "Serveur inaccessible. Vérifie ta connexion."
                      : "Connexion au serveur en cours…"}
                  </p>
                )}
              </motion.form>
            </motion.section>
          )}
        </AnimatePresence>
      </div>
    </main>
  );
};
